import { ApolloError } from '@apollo/client'
import { useTranslation } from 'react-i18next'

/** First `graphQLErrors` message when `error` is an `ApolloError`; otherwise `undefined`. */
export function getFirstGraphQlErrorMessage(
  error: unknown
): string | undefined {
  if (error instanceof ApolloError) {
    return error.graphQLErrors[0]?.message
  }
  return undefined
}

export const useErrorTranslation = () => {
  const { t, i18n } = useTranslation('errors')
  return (error: unknown) => {
    if (error === undefined) {
      return ''
    }
    if (typeof error == 'string') {
      return t(error)
    }

    if (error instanceof ApolloError) {
      const { graphQLErrors, networkError } = error

      if (networkError) {
        return t(networkError.message)
      }

      if (graphQLErrors?.[0]) {
        const code = graphQLErrors[0].extensions?.code
        const tCode = t(code as string)
        if (code && tCode != code) {
          return tCode
        }
        // The `t` will parse or cut the original error.
        return i18n.exists(`errors.${graphQLErrors[0].message}`)
          ? t(graphQLErrors[0].message)
          : graphQLErrors[0].message
      }

      return t(error.message)
    }
    if (error instanceof Error) {
      return t(error.message)
    }

    return t('UNKNOWN')
  }
}
export const getErrorCode = (error: any) => {
  if (error instanceof ApolloError) {
    const { graphQLErrors } = error
    if (graphQLErrors) {
      return graphQLErrors[0]?.extensions?.code
    }
  }
  return undefined
}

export const getApolloErrorCode = (error: ApolloError, index = 0) => {
  console.error(error.graphQLErrors?.[index])
  return error.graphQLErrors?.[index]?.extensions?.code
}

export const ERROR_CODES = {
  METHOD_NOT_ALLOWED: '405',
  UNAUTHORIZED: '401',
  PRECONDITION_FAILED: '412',
  PAYMENT_REQUIRED: 'PAYMENT_REQUIRED',
  FORBIDDEN: '403',
  NOT_FOUND: '404',
  INTERNAL_SERVER_ERROR: '500',
  UNPROCESSABLE_ENTITY: '422',
}
export const hasApolloErrorCode = (
  error: ApolloError,
  code: string | string[]
) => {
  code = Array.isArray(code) ? code : [code]
  return error.graphQLErrors?.some(
    (e) =>
      typeof e.extensions?.code === 'string' &&
      code.includes(e.extensions?.code)
  )
}
